/* ESTASOFT theme runtime. Vanilla JS, no dependencies. */
(() => {
  const root = document.documentElement;
  const designMode = Boolean(window.Shopify && window.Shopify.designMode);
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---- fade sections in once as they enter the viewport ---- */
  const revealObserver = 'IntersectionObserver' in window && !designMode && !reducedMotion
    ? new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('is-in');
        revealObserver.unobserve(entry.target);
      }
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0 }) /* 0, not a share of the block: a very tall block (a long legal page on a phone) never reaches 5% of its own height */
    : null;

  /* ---- pause mockup loops while off screen ---- */
  const loopObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) entry.target.removeAttribute('data-paused');
        else entry.target.setAttribute('data-paused', '');
      }
    }, { rootMargin: '120px' })
    : null;

  /** @param {ParentNode} scope */
  const observe = (scope) => {
    for (const el of scope.querySelectorAll('[data-reveal]:not(.is-in)')) {
      if (revealObserver) revealObserver.observe(el);
      else el.classList.add('is-in');
    }
    if (loopObserver) for (const el of scope.querySelectorAll('.vis, .thumb, .prog__list, .chwall__band')) loopObserver.observe(el);
  };

  /* ---- dialogs: any element with data-dialog-open="<id>" opens <dialog id="<id>"> ---- */
  /** @type {Map<string, HTMLElement>} */
  const openers = new Map();

  /** @param {string} id @param {HTMLElement} [opener] */
  const openDialog = (id, opener) => {
    const dialog = document.getElementById(id);
    if (!(dialog instanceof HTMLDialogElement)) return false;
    if (opener) openers.set(id, opener);
    if (!dialog.open) dialog.showModal();
    root.classList.add('no-scroll');
    dialog.dispatchEvent(new CustomEvent('dialog:open', { bubbles: true }));
    return true;
  };

  /** @param {HTMLDialogElement} dialog */
  const closeDialog = (dialog) => {
    if (dialog.open) dialog.close();
  };

  /* a click only closes a dialog when it also started on the backdrop, so dragging a text selection out of the dialog does not close it */
  let downOnBackdrop = false;
  document.addEventListener('pointerdown', (event) => {
    downOnBackdrop = event.target instanceof HTMLDialogElement;
  }, true);

  document.addEventListener('click', (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const opener = target.closest('[data-dialog-open]');
    if (opener) {
      if (openDialog(opener.getAttribute('data-dialog-open') || '', /** @type {HTMLElement} */ (opener))) event.preventDefault();
      return;
    }
    const closer = target.closest('[data-dialog-close]');
    if (closer) {
      const dialog = closer.closest('dialog');
      if (dialog) closeDialog(dialog);
      return;
    }
    // click on the backdrop (the dialog element itself) closes it
    if (downOnBackdrop && target instanceof HTMLDialogElement && target.hasAttribute('data-backdrop-close')) closeDialog(target);
  });

  document.addEventListener('close', (event) => {
    const dialog = event.target;
    if (!(dialog instanceof HTMLDialogElement)) return;
    if (!document.querySelector('dialog[open]')) root.classList.remove('no-scroll');
    // return focus to the opener, once, and only if it is still on the page and visible
    const opener = openers.get(dialog.id);
    openers.delete(dialog.id);
    if (opener && document.contains(opener) && opener.getClientRects().length) opener.focus();
    // clear a dialog hash so the same link can open it again
    if (hashDialogs[window.location.hash] === dialog.id) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  }, true);

  /* open a dialog from the URL hash, for example /#search or /#book */
  const hashDialogs = { '#search': 'search-dialog', '#book': 'booking-dialog', '#language': 'language-dialog', '#cookies': 'cookie-dialog' };
  const openFromHash = () => {
    const id = hashDialogs[window.location.hash];
    if (id) openDialog(id);
  };
  window.addEventListener('hashchange', openFromHash);

  /* a link that opens the cookie window is hidden when the cookie window is switched off, so it is never a dead link */
  const hideDeadCookieLinks = () => {
    if (document.getElementById('cookie-dialog')) return;
    for (const link of document.querySelectorAll('a[data-dialog-open="cookie-dialog"]')) {
      /** @type {HTMLElement} */ (link.closest('li') || link).hidden = true;
    }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', hideDeadCookieLinks);
  else hideDeadCookieLinks();

  /* ---- partner strip: load the original-color logos on the first hover or focus ---- */
  /** @param {Event} event */
  const loadPartnerColors = (event) => {
    const target = event.target;
    const grid = target instanceof Element ? target.closest('.partners__grid') : null;
    if (!grid) return;
    for (const img of grid.querySelectorAll('img[data-color-src]')) {
      if (img instanceof HTMLImageElement && img.dataset.colorSrc) img.src = img.dataset.colorSrc;
      img.removeAttribute('data-color-src');
    }
  };
  document.addEventListener('pointerover', loadPartnerColors, { passive: true });
  document.addEventListener('focusin', loadPartnerColors);

  /* ---- init ---- */
  const init = () => {
    observe(document);
    openFromHash();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  /* ---- theme editor: while a section with a dialog is selected, show that dialog ----
     The header menu is left out (it would cover the header being edited). The cookie notice shows its own banner. */
  const EDITOR_DIALOGS = ['booking-dialog', 'search-dialog', 'language-dialog'];
  let selectedSection = '';

  /** @param {EventTarget | null} scope @returns {HTMLDialogElement[]} */
  const editorDialogs = (scope) => {
    if (!(scope instanceof Element)) return [];
    return Array.from(scope.querySelectorAll('dialog')).filter((d) => d instanceof HTMLDialogElement && EDITOR_DIALOGS.includes(d.id));
  };
  /** @param {EventTarget | null} scope */
  const showEditorDialogs = (scope) => {
    for (const dialog of editorDialogs(scope)) if (!dialog.open) openDialog(dialog.id);
  };
  /** @param {EventTarget | null} scope */
  const hideEditorDialogs = (scope) => {
    for (const dialog of editorDialogs(scope)) closeDialog(dialog);
    // a section that is re-rendered while its own dialog is open (for example the header menu) never fires a close event
    if (scope instanceof Element) for (const dialog of scope.querySelectorAll('dialog[open]')) if (dialog instanceof HTMLDialogElement) dialog.close();
    if (!document.querySelector('dialog[open]')) root.classList.remove('no-scroll');
  };

  document.addEventListener('shopify:section:select', (event) => {
    const el = event.target;
    selectedSection = el instanceof Element ? el.id : '';
    showEditorDialogs(el);
  });
  document.addEventListener('shopify:section:deselect', (event) => {
    const el = event.target;
    if (el instanceof Element && el.id === selectedSection) selectedSection = '';
    hideEditorDialogs(el);
  });
  document.addEventListener('shopify:section:unload', (event) => hideEditorDialogs(event.target));

  document.addEventListener('shopify:section:load', (event) => {
    const el = /** @type {HTMLElement} */ (event.target);
    for (const node of el.querySelectorAll('[data-reveal]')) node.classList.add('is-in');
    observe(el);
    // a selected section was re-rendered after a setting change: keep its dialog open
    if (selectedSection && el.id === selectedSection) showEditorDialogs(el);
  });

  /** Public helpers for section scripts. */
  window.EstaTheme = { openDialog, closeDialog, observe };
})();
